import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-phone-input-2/lib/style.css";
import Swal from "sweetalert2";
import {
  fetchAddressList,
  deleteAddress,
  setSelectedAddress,
  setIsEditing,
  setCurrentAddress,
  setAddressList,
  addGuestAddress,
  updateGuestAddress,
} from "./features/addressSlice";

const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

const Address1 = ({ onAddressSelect }) => {
  const dispatch = useDispatch();
  const locationData = useSelector((state) => state.location.locationData);
  const user = useSelector((state) => state.auth.user);
  const [addressToEdit, setAddressToEdit] = useState(null);
  const { addressList, selectedAddressId, isEditing, currentAddress } =
    useSelector((state) => state.address);

  // console.log(addressList, "addressList from address1");

  const userId = user?._id;
  const [showAll, setShowAll] = useState(false);

  const [values, setValues] = useState({
    userId: userId,
    type: "Residential",
    buildingName: "",
    street: "",
    landmark: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
    isDefault: false,
  });

  const validationPatterns = {
    buildingName: /^[a-zA-Z0-9\s\-.,#]{2,50}$/,
    street: /^[a-zA-Z0-9\s\-.,#]{5,100}$/,
    landmark: /^[a-zA-Z0-9\s\-.,#]{0,50}$/,
    city: /^[a-zA-Z\s]{2,30}$/,
    postalCode: /^\d{6}$/,
    country: /^[a-zA-Z\s]{2,30}$/,
  };

  const [errors, setErrors] = useState({});

  const [postOfficeOptions, setPostOfficeOptions] = useState([]);
  const [showPostOfficeSelect, setShowPostOfficeSelect] = useState(false);

  const [postalCodeEntered, setPostalCodeEntered] = useState(false);
  const [locationFields, setLocationFields] = useState({
    village: "",
    district: "",
    state: "",
  });

  const resetForm = () => {
    setValues({
      userId: userId,
      type: "Residential",
      buildingName: "",
      street: "",
      landmark: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
      isDefault: false,
    });
    setPostalCodeEntered(false);
    setShowPostOfficeSelect(false);
    setPostOfficeOptions([]);
    setLocationFields({
      village: "",
      district: "",
      state: "",
    });
    setIsEditing(false);
    setCurrentAddress(null);
  };

  useEffect(() => {
    if (userId) {
      dispatch(fetchAddressList()).then((selectedId) => {
        if (selectedId) {
          onAddressSelect(selectedId);
        }
      });
    } else {
      // Load addresses from localStorage for non-logged in users
      const localAddresses = JSON.parse(
        localStorage.getItem("guestAddresses") || "[]"
      );
      if (localAddresses.length > 0) {
        // dispatch(setAddressList(localAddresses));
        dispatch(setSelectedAddress(localAddresses[0]._id));
        onAddressSelect(localAddresses[0]._id);
      }
    }
  }, [dispatch, userId, onAddressSelect]);

  // Add a new useEffect to handle address list changes
  useEffect(() => {
    if (addressList?.length > 0) {
      // If we have addresses but no selected address, select the first one
      if (!selectedAddressId) {
        const defaultAddress = addressList.find(addr => addr.isDefault) || addressList[0];
        dispatch(setSelectedAddress(defaultAddress._id));
        onAddressSelect(defaultAddress._id);
      }
    }
  }, [addressList, selectedAddressId, dispatch, onAddressSelect]);

  const validateField = (name, value) => {
    if (name === "state") return true;
    if (!validationPatterns[name]) return true;
    return validationPatterns[name].test(value);
  };

  const fetchAddressFromPincode = async (pincode) => {
    try {
      const response = await fetch(
        `https://api.postalpincode.in/pincode/${pincode}`
      );
      const data = await response.json();

      if (data[0]?.Status === "Success" && data[0]?.PostOffice?.length > 0) {
        setPostalCodeEntered(true);

        if (data[0].PostOffice.length === 1) {
          // Single location - directly set and disable all fields
          const location = data[0].PostOffice[0];
          setLocationFields({
            village: location.Name,
            district: location.District,
            state: location.State,
          });
          setShowPostOfficeSelect(false);
          setPostOfficeOptions([]);
          setValues((prev) => ({
            ...prev,
            city: location.Name,
            state: location.State,
            country: "India",
          }));
        } else {
          // Multiple locations - show dropdown for village/city
          setPostOfficeOptions(data[0].PostOffice);
          setShowPostOfficeSelect(true);
          // Set district and state from first location (they'll be same for all)
          const firstLocation = data[0].PostOffice[0];
          setLocationFields({
            village: "", // Will be selected from dropdown
            district: firstLocation.District,
            state: firstLocation.State,
          });
          setValues((prev) => ({
            ...prev,
            city: "", // Will be set when village is selected
            state: firstLocation.State,
            country: "India",
          }));
        }
      } else {
        toast.error("No location found for this pincode");
        setPostalCodeEntered(false);
        setShowPostOfficeSelect(false);
        setPostOfficeOptions([]);
      }
    } catch (error) {
      console.error("Error fetching address:", error);
      toast.error("Error fetching location details");
      setPostalCodeEntered(false);
      setShowPostOfficeSelect(false);
      setPostOfficeOptions([]);
    }
  };

  const handlePostOfficeSelection = (e) => {
    const selectedLocation = postOfficeOptions.find(
      (po) => po.Name === e.target.value
    );

    if (selectedLocation) {
      setLocationFields((prev) => ({
        ...prev,
        village: selectedLocation.Name,
      }));
      setValues((prev) => ({
        ...prev,
        city: selectedLocation.Name,
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const isValid = type === "checkbox" ? true : validateField(name, value);

    setErrors((prev) => ({
      ...prev,
      [name]: !isValid,
    }));

    setValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "postalCode" && value.length === 6) {
      fetchAddressFromPincode(value);
    }
  };

  const handleEditAddress = async (addressId) => {
    const addressToEdit = addressList.find((addr) => addr._id === addressId);
    setAddressToEdit(addressToEdit);
    if (addressToEdit) {
      dispatch(setIsEditing(true));
      dispatch(setCurrentAddress(addressToEdit));

      setValues({
        userId: userId,
        type: addressToEdit.type || "Residential",
        buildingName: addressToEdit.buildingName || "",
        street: addressToEdit.street || "",
        landmark: addressToEdit.landmark || "",
        city: addressToEdit.city || "",
        state: addressToEdit.state || "",
        postalCode: addressToEdit.postalCode || "",
        country: addressToEdit.country || "",
        isDefault: addressToEdit.isDefault || false,
      });

      // Set locationFields when editing
      setLocationFields({
        village: addressToEdit.city || "", // Using city as village since we don't store village separately
        district: addressToEdit.district || "",
        state: addressToEdit.state || "",
      });

      const addressModal = document.querySelector("#add-address");
      const bsModal = new window.bootstrap.Offcanvas(addressModal);
      bsModal.show();
    } else {
      toast.error("Address not found");
    }
  };

  const handleDeleteAddress = (addressId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this address!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteAddress(addressId));
      }
    });
  };

  const handleAddressSelection = (addressId) => {
    console.log("selected addressId from address1", addressId);
    dispatch(setSelectedAddress(addressId));
    onAddressSelect(addressId);
  };

  const handleCreateAddress = async (e) => {
    e.preventDefault();
    try {
      const addressData = {
        type: values.type,
        buildingName: values.buildingName,
        street: values.street,
        landmark: values.landmark,
        city: values.city,
        district: locationFields.district,
        state: values.state,
        postalCode: values.postalCode,
        country: values.country,
        isDefault: values.isDefault,
      };

      if (!addressData.district) {
        toast.error("Please enter postal code and select location first");
        return;
      }

      if (localStorage.getItem("token")) {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/addresses`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify(addressData),
          }
        );

        const data = await res.json();

        if (data.success) {
          toast.success("Address added successfully");
          dispatch(fetchAddressList());
          resetForm();
        } else {
          toast.error(data.message || "Failed to save address");
        }
      } else {
        // For guest users, only store in localStorage and Redux
        const guestAddress = {
          _id: Date.now().toString(),
          ...addressData,
          createdAt: new Date().toISOString(),
        };
        dispatch(addGuestAddress(guestAddress));
        // Automatically select the newly added address if it's the first one
        if (!selectedAddressId) {
          dispatch(setSelectedAddress(guestAddress._id));
          onAddressSelect(guestAddress._id);
        }
        toast.success("Address added successfully");
        resetForm();
      }

      const offcanvasElement = document.getElementById("add-address");
      const offcanvasInstance =
        window.bootstrap.Offcanvas.getInstance(offcanvasElement);
      if (offcanvasInstance) {
        offcanvasInstance.hide();
      }
    } catch (error) {
      console.error("Address save error:", error);
      toast.error("Failed to save address");
    }
  };

  const handleUpdateAddress = async (e) => {
    e.preventDefault();
    try {
      const addressData = {
        userId: userId,
        type: values.type,
        buildingName: values.buildingName,
        street: values.street,
        landmark: values.landmark,
        city: values.city,
        district: locationFields.district,
        state: values.state,
        postalCode: values.postalCode,
        country: values.country,
        isDefault: values.isDefault,
      };

      if (!addressData.district) {
        toast.error("Please enter postal code and select location first");
        return;
      }

      console.log("Address page token, ",  localStorage.getItem("token"));
      if (localStorage.getItem("token")) {
        // For logged-in users, update on server
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/addresses/${addressToEdit._id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify(addressData),
          }
        );

        const data = await res.json();

        if (data.success) {
          toast.success("Address updated successfully");
          dispatch(fetchAddressList());

          const offcanvasElement = document.getElementById("add-address");
          const offcanvasInstance =
            window.bootstrap.Offcanvas.getInstance(offcanvasElement);
          if (offcanvasInstance) {
            offcanvasInstance.hide();
          }

          resetForm();
          dispatch(setIsEditing(false));
          dispatch(setCurrentAddress(null));
        } else {
          toast.error(data.message || "Failed to update address");
        }
      } else {
        // For non-logged-in users, update locally
        const updatedAddress = {
          ...addressToEdit,
          ...addressData,
          updatedAt: new Date().toISOString(),
        };

        // Update in Redux store
        dispatch(updateGuestAddress(updatedAddress));
        
        toast.success("Address updated successfully");
        
        const offcanvasElement = document.getElementById("add-address");
        const offcanvasInstance =
          window.bootstrap.Offcanvas.getInstance(offcanvasElement);
        if (offcanvasInstance) {
          offcanvasInstance.hide();
        }

        resetForm();
        dispatch(setIsEditing(false));
        dispatch(setCurrentAddress(null));
      }
    } catch (error) {
      console.error("Address update error:", error);
      toast.error("Failed to update address");
    }
  };

  const toggleView = () => {
    setShowAll(!showAll);
  };

  useEffect(() => {
    if (currentAddress && isEditing) {
      setValues({
        userId: userId,
        type: currentAddress.type || "Residential",
        buildingName: currentAddress.buildingName || "",
        street: currentAddress.street || "",
        landmark: currentAddress.landmark || "",
        city: currentAddress.city || "",
        state: currentAddress.state || "",
        postalCode: currentAddress.postalCode || "",
        country: currentAddress.country || "",
        isDefault: currentAddress.isDefault || false,
      });
    } else if (!isEditing) {
      resetForm();
    }
  }, [currentAddress, isEditing, userId, locationData]);

  useEffect(() => {
    const addressModal = document.getElementById("add-address");
    if (addressModal) {
      addressModal.addEventListener("hidden.bs.offcanvas", resetForm);
      return () => {
        addressModal.removeEventListener("hidden.bs.offcanvas", resetForm);
      };
    }
  }, []);

  return (
    <>
      <div className="checkout-add p-0">
        <h4>
          <span>Address</span>
          <button
            type="button"
            data-bs-toggle="offcanvas"
            data-bs-target="#add-address"
            className="thm-btn"
          >
            Add New Address
          </button>
        </h4>
        <div className="p-3">
          {addressList?.length === 0 ? (
            <div className="text-center">
              <p className="fz-16 txt-dark">
                No addresses available. Please add a new address.
              </p>
            </div>
          ) : (
            <>
              {addressList?.map((address, index) => (
                <div className="checkout-add-innr" key={address._id}>
                  <div className="select-add">
                    <h5 className="fz-20 fw-600">Address {index + 1}</h5>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="address"
                        id={address._id}
                        checked={selectedAddressId === address._id}
                        onChange={() => handleAddressSelection(address._id)}
                      />
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="row">
                      <div className="d-flex justify-content-end">
                        <button
                          type="button"
                          onClick={() => handleEditAddress(address._id)}
                          className="txt-org bg-none bg-0"
                          style={{ background: 0 }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteAddress(address._id)}
                          className="text-danger bg-none bg-0"
                          style={{ background: 0 }}
                        >
                          Delete
                        </button>
                      </div>
                      <div className="col-lg-12">
                        <p>
                          <strong>{address.type}</strong>
                          <br />
                          {address.buildingName},<br />
                          {address.street},
                          {address.landmark && (
                            <span> {address.landmark}, </span>
                          )}
                          {address.city}, {address.state}
                          <br />
                          {address.district} - {address.postalCode}, {address.country}
                          {address.isDefault && (
                            <span className="p-2 rounded badge text-white bg-success ms-2">
                              Default
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {addressList?.length > 2 && (
                <div className="text-end mt-2">
                  <button type="button" onClick={toggleView} className="clr">
                    {showAll ? "View Less" : "View More"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Address Form Modal */}
      <div
        className="offcanvas offcanvas-end address-canvas"
        tabIndex={-1}
        id="add-address"
        aria-labelledby="offcanvasRightLabel"
      >
        <div className="offcanvas-header" style={{ background: "#5B4B88" }}>
          <h5 id="offcanvasRightLabel" className="fw-bold">
            {isEditing ? "Edit Address" : "Add New Address"}
          </h5>
          <button
            type="button"
            className="btn-close text-reset"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
            onClick={resetForm}
          >
            <i className="fa fa-times" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form
            onSubmit={isEditing ? handleUpdateAddress : handleCreateAddress}
          >
            <div className="address-canvas-frm">
              <div className="row g-3">
                <div className="col-12">
                  <div className="custom-frm-bx">
                    <label>Address Type</label>
                    <select
                      name="type"
                      value={values.type}
                      onChange={handleChange}
                      className="form-select"
                    >
                      <option value="Residential">Residential</option>
                      <option value="Office">Office</option>
                    </select>
                  </div>
                </div>

                <div className="col-12">
                  <div className="custom-frm-bx">
                    <label>Building Name</label>
                    <span className="text-danger"> *</span>
                    <input
                      type="text"
                      name="buildingName"
                      value={values.buildingName}
                      onChange={handleChange}
                      className={`form-control ${
                        errors.buildingName ? "is-invalid" : ""
                      }`}
                      required
                      maxLength={50}
                      placeholder="Enter building name"
                    />
                    {errors.buildingName && (
                      <div className="invalid-feedback">
                        Please enter a valid building name (2-50 characters,
                        alphanumeric)
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-12">
                  <div className="custom-frm-bx">
                    <label>Street</label>
                    <span className="text-danger"> *</span>
                    <input
                      type="text"
                      name="street"
                      value={values.street}
                      onChange={handleChange}
                      className={`form-control ${
                        errors.street ? "is-invalid" : ""
                      }`}
                      required
                      maxLength={100}
                      placeholder="Enter street address"
                    />
                    {errors.street && (
                      <div className="invalid-feedback">
                        Please enter a valid street address (5-100 characters)
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-12">
                  <div className="custom-frm-bx">
                    <label>Landmark</label>
                    <input
                      type="text"
                      name="landmark"
                      value={values.landmark}
                      onChange={handleChange}
                      className={`form-control ${
                        errors.landmark ? "is-invalid" : ""
                      }`}
                      maxLength={50}
                      placeholder="Enter nearby landmark (optional)"
                    />
                    {errors.landmark && (
                      <div className="invalid-feedback">
                        Please enter a valid landmark (up to 50 characters)
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="custom-frm-bx">
                    <label>Postal Code</label>
                    <span className="text-danger"> *</span>
                    <input
                      type="text"
                      name="postalCode"
                      value={values.postalCode}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d{0,6}$/.test(value)) {
                          handleChange(e);
                        }
                      }}
                      className="form-control"
                      required
                      placeholder="Enter postal code"
                    />
                  </div>
                </div>

                {postalCodeEntered && (
                  <>
                    <div className="col-md-6">
                      <div className="custom-frm-bx">
                        <label>Village/City</label>
                        <span className="text-danger"> *</span>
                        {showPostOfficeSelect ? (
                          // Dropdown for multiple locations
                          <select
                            className="form-select"
                            onChange={handlePostOfficeSelection}
                            required
                          >
                            <option value="">Select Village/City</option>
                            {postOfficeOptions.map((po) => (
                              <option key={po.Name} value={po.Name}>
                                {po.Name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          // Disabled input for single location
                          <input
                            type="text"
                            className="form-control"
                            value={locationFields.village}
                            disabled
                          />
                        )}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="custom-frm-bx">
                        <label>District</label>
                        <span className="text-danger"> *</span>
                        <input
                          type="text"
                          className="form-control"
                          value={locationFields.district}
                          disabled
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="custom-frm-bx">
                        <label>State</label>
                        <span className="text-danger"> *</span>
                        <input
                          type="text"
                          className="form-control"
                          value={locationFields.state}
                          disabled
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="col-md-6">
                  <div className="custom-frm-bx">
                    <label>Country</label>
                    <span className="text-danger"> *</span>
                    <select
                      name="country"
                      value={values.country}
                      onChange={handleChange}
                      className="form-select"
                      maxLength={20}
                      required
                    >
                      <option selected value="India">
                        India
                      </option>
                    </select>
                  </div>
                </div>

                <div className="col-12">
                  <div className="custom-frm-bx">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="isDefault"
                        name="isDefault"
                        checked={values.isDefault}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="isDefault">
                        Set as default address
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-end mt-4 mb-4">
              {isEditing ? (
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  onClick={handleUpdateAddress}
                >
                  Update Address
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  onClick={handleCreateAddress}
                >
                  Save Address
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default Address1;
